import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { IAutoDownloadSetting } from "@app/interfaces/IAutoDownloadSetting";
import { CameraLocation } from "@app/main/components/videos/enums/CameraLocation";
import { getValuesFromEnum } from "@app/class/Utilities";
import { VideoEventCode } from "@app/main/components/videos/enums/VideoEventCode";
import { AutoDownloadSettingService } from "@app/services/autoDownloadSetting.service";
import { TranslationService } from "@app/services/translation.service";
import './auto-download-configuration-editor.component.scss';

@Component({
    selector: 'auto-download-configuration-editor',
    template: require('./auto-download-configuration-editor.component.html')
})
export class AutoDownloadConfigurationEditorComponent implements OnInit {

    eventTypes: { label: string, value: number }[] = [];
    channelsList: { label: string, value: number, isChecked: boolean }[] = [];
    videoDurations: { label: string, value: number }[];

    @Input() setting: IAutoDownloadSetting;
    @Input() filter: VideoEventCode[];
    @Output() onSettingSaved = new EventEmitter<IAutoDownloadSetting>();

    autoDownloadForm: FormGroup;
    dataUsageInformation = "";
    numberOfEvents = 0;
    numberOfVehiclesInFleet = 0;
    camerasSelected = 0;

    get eventType(): AbstractControl {
        return this.autoDownloadForm?.get("eventType");
    }

    get channels(): AbstractControl {
        return this.autoDownloadForm?.get("channels");
    }

    get areChannelsValid(): boolean {
        return this.autoDownloadForm.controls["channels"].valid
    }

    constructor(
        private readonly translationService: TranslationService,
        private readonly autoDownloadSettingService: AutoDownloadSettingService,
        private readonly formBuilder: FormBuilder
    ) { }

    ngOnInit(): void {
        this.setOptions();

        this.getNumberOfVehicles();
        this.getNumberOfEvents(this.setting.EventType);
        this.buildForm();
    }

    onEventTypeChange(value: number): void {
        this.eventType.setValue(value);
        this.eventType.updateValueAndValidity();

        this.getNumberOfEvents(value);
    }

    onChannelChecked(value: number, isChecked: boolean): void {
        const { Channels } = this.setting;

        if (isChecked) {
            if (!Channels.includes(value)) {
                Channels.push(value);
                this.camerasSelected++;
            }
        } else {
            const index = Channels.indexOf(value);
            if (index > -1) {
                Channels.splice(index, 1);
                this.camerasSelected--;
            }
        }

        this.channels.updateValueAndValidity();

        if (this.setting.EventType != null) {
            this.setDataUsageInformation();
        }
    }

    saveSetting(): void {
        this.autoDownloadForm.updateValueAndValidity();

        if (this.autoDownloadForm.valid) {
            this.autoDownloadSettingService.save(this.setting).subscribe(() => {
                this.onSettingSaved.emit();
            });
        }
    }

    deleteSetting(confirmed: boolean): void {
        if (!confirmed) {
            return;
        }

        if (this.setting) {
            this.autoDownloadSettingService.delete(this.setting.Id).subscribe(() => {
                this.onSettingSaved.emit();
            });
        }
    }

    private setOptions(): void {
        if (!this.filter) {
            this.filter = []; 
        }

        const excludeVideoEventTypes = [VideoEventCode.Unknown, VideoEventCode.Motion, VideoEventCode.Exception, VideoEventCode.SpeedWarning];
        this.filter.push(...excludeVideoEventTypes);
        
        const videoEventValues = getValuesFromEnum(VideoEventCode);

        videoEventValues.forEach(value => {
            if (!this.filter.includes(value) || value === this.setting.EventType) {
                this.eventTypes.push({
                    label: this.translationService.translateEnum(VideoEventCode, "VideoEventCodeNewTranslations", value),
                    value
                });
            };
        });

        this.eventTypes = this.eventTypes.sort((a,b) => a.label.localeCompare(b.label));        

        const channelValues = getValuesFromEnum(CameraLocation);
        channelValues.forEach(value => {
            const isChecked = this.setting.Channels.includes(value);
            if (isChecked) { this.camerasSelected++; }

            this.channelsList.push({
                label: this.translationService.translateEnum(CameraLocation, "CameraLocation", value),
                value,
                isChecked
            });
        });

        this.videoDurations = [];
        this.videoDurations.push({ label: "10s", value: 10 });
        this.videoDurations.push({ label: "20s", value: 20 });
        this.videoDurations.push({ label: "30s", value: 30 });
    }

    private setDataUsageInformation(): void {
        if (this.numberOfEvents >= 0 && this.camerasSelected > 0) {
            const HdDataUsage = 0.4522667; //MB per second
            const SdDataUsage = 0.3384889;

            const dataUsagePerSecond = this.setting.IsHighDefinition ? HdDataUsage : SdDataUsage;

            const totalDataUsage = this.numberOfVehiclesInFleet > 0 ? ((this.numberOfEvents * dataUsagePerSecond * this.camerasSelected * this.setting.VideoDuration) / this.numberOfVehiclesInFleet) : 0;

            const dataUsageMessage = totalDataUsage < 1 ? "less than 1" : totalDataUsage.toFixed(0);

            this.dataUsageInformation = this.numberOfEvents == 1 ?
                this.translationService.t("AutoDownloadConfiguration.DataUsageInformationSingular", [dataUsageMessage]) :
                this.translationService.t("AutoDownloadConfiguration.DataUsageInformationPlural", [this.numberOfEvents, dataUsageMessage]
                );
        } else {
            this.dataUsageInformation = "";
        }
    }

    private getNumberOfEvents(eventType: number): void {
        if (eventType >= 0) {
            this.autoDownloadSettingService.getNumberOfEvents(eventType)
                .subscribe(response => {
                    this.numberOfEvents = response;
                    this.setDataUsageInformation();
                });
        }
    }

    private getNumberOfVehicles(): void {
        this.autoDownloadSettingService.getNumberOfVehicles()
            .subscribe(response => {
                this.numberOfVehiclesInFleet = response;
            });
    }

    private buildForm(): void {
        this.autoDownloadForm = this.formBuilder.group({
            eventType: [this.setting.EventType, [Validators.required]],
            channels: [this.setting.Channels, [this.validateIfChannelSeleceted()]]
        });
    }

    private validateIfChannelSeleceted(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!this.autoDownloadForm) {
                return null;
            }

            if (this.camerasSelected === 0) {
                return { customMessage: this.translationService.t("AutoDownloadConfiguration.ChannelsValidationFail") }
            }

            return null;
        };
    }

}
